import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { TextField } from "@shared/components/TextField";
import styled from "@emotion/styled";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  SubscriptionUserInfoSchema,
  getSubscriptionUserInfoFormSchema,
} from "./subscription-user-info-form-helpers";
import { Form } from "@shared/components/Form";
import { useState } from "react";
import { SubscriptionFlowChangeEmail } from "@features/Subscription/components/SubscriptionFlowChangeEmail";
import { SubscriptionFlowStatus } from "@features/Subscription/subscription.types";
import { PhoneNumberInput } from "@shared/components/PhoneNumberInput";
import { SubscriptionSubmitButton } from "../styles";
import {
  SubscriptionFlowConfigDto,
  SubscriptionFlowFieldDto,
  SubscriptionFlowFieldDtoTypeEnum,
} from "@shared/apis/sidekick/generated";
import CheckIcon from "@mui/icons-material/CheckCircle";

const AdornmentButton = styled(Button)`
  display: flex;
  gap: 0.25rem;
  margin-right: 0.25rem;
  padding: 0.5rem 0.75rem;
`;

const AdornmentHelp = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.75rem;
  cursor: default;
`;

const ReadOnlyText = styled(Typography)`
  padding: 0.5rem 0 1rem 0rem;
`;

const ReadOnlyInfo = (props: { label: string; value: string }) => {
  const { label, value } = props;
  return (
    <FormControl fullWidth>
      <FormLabel>{label}</FormLabel>
      <ReadOnlyText>{value}</ReadOnlyText>
    </FormControl>
  );
};

type SubscriptionUserInfoDefaultValues = {
  email: string;
  mlsId: string;
  name: string;
  phoneNumber: string;
  officeName: string;
};

type SubscriptionUserInfoFormProps = {
  title: string;
  subscriptionId: string;
  subscriptionFlowStatus: SubscriptionFlowStatus;
  defaultValues: SubscriptionUserInfoDefaultValues;
  subscriptionFlowConfig: SubscriptionFlowConfigDto;
  canEditEmail: boolean;
  submitButtonText?: string;
  onFormSubmit: (data: SubscriptionUserInfoSchema) => Promise<void>;
  requestIsLoading: boolean;
};

export const SubscriptionUserInfoForm = (
  props: SubscriptionUserInfoFormProps,
) => {
  const [openChangeEmailModal, setOpenChangeEmailModal] = useState(false);
  const {
    title,
    defaultValues,
    subscriptionId,
    subscriptionFlowStatus,
    onFormSubmit,
    requestIsLoading,
    subscriptionFlowConfig,
    canEditEmail,
    submitButtonText = "Next",
  } = props;

  const fields = subscriptionFlowConfig.stepUserInfo;

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<SubscriptionUserInfoSchema>({
    defaultValues: {
      name: defaultValues.name,
      mlsId: defaultValues.mlsId,
      phoneNumber: defaultValues.phoneNumber,
      officeName: defaultValues.officeName,
    },
    resolver: yupResolver(
      getSubscriptionUserInfoFormSchema(subscriptionFlowConfig),
    ),
    reValidateMode: "onChange",
  });

  const getField = (name: string): SubscriptionFlowFieldDto | undefined =>
    fields.find((f) => f.name === name);

  const getFieldLabel = (name: string): string => {
    const field = getField(name);
    if (!field) return "";
    return `${field.label}${field.optional ? " (optional)" : ""}`;
  };

  const handleOpenChangeEmailModal = () => {
    setOpenChangeEmailModal(true);
  };

  const nameField = getField("name");
  const mlsIdField = getField("mlsId");
  const phoneNumberField = getField("phoneNumber");
  const officeNameField = getField("officeName");
  const emailField = getField("email");

  return (
    <div>
      <Form
        onSubmit={handleSubmit(onFormSubmit)}
        aria-disabled={requestIsLoading}
      >
        <Box mb={3}>
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Grid container columnSpacing={3} rowSpacing={1}>
          {nameField && (
            <Grid item md={6} sm={12} xs={12}>
              {nameField.readOnly ? (
                <ReadOnlyInfo
                  label={nameField.label}
                  value={defaultValues.name}
                />
              ) : (
                <TextField
                  label={getFieldLabel("name")}
                  name="name"
                  inputProps={{ ...register("name") }}
                  fieldError={errors.name}
                />
              )}
            </Grid>
          )}

          {phoneNumberField && (
            <Grid item md={6} sm={12} xs={12}>
              {phoneNumberField.readOnly ? (
                <ReadOnlyInfo
                  label={phoneNumberField.label}
                  value={defaultValues.phoneNumber}
                />
              ) : phoneNumberField.type ===
                SubscriptionFlowFieldDtoTypeEnum.PhoneNumber ? (
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <PhoneNumberInput
                      label={getFieldLabel("phoneNumber")}
                      name="phoneNumber"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      fieldError={errors.phoneNumber}
                    />
                  )}
                />
              ) : (
                <TextField
                  label={getFieldLabel("phoneNumber")}
                  name="phoneNumber"
                  inputProps={{ ...register("phoneNumber") }}
                  fieldError={errors.phoneNumber}
                />
              )}
            </Grid>
          )}

          {emailField && (
            <Grid item xs={12}>
              <TextField
                label={getFieldLabel("email")}
                name="email"
                value={defaultValues.email}
                InputProps={{
                  readOnly: true,
                  endAdornment: canEditEmail ? (
                    <InputAdornment position="end">
                      {subscriptionFlowStatus === "payment-setup-pending" ? (
                        <Tooltip
                          title={
                            "You can not change your email when payment is pending."
                          }
                        >
                          <AdornmentHelp>
                            <HelpOutlineIcon color="secondary" />
                          </AdornmentHelp>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            "You have the option to use a different email address for your Sidekick account."
                          }
                        >
                          <AdornmentButton
                            variant="text"
                            size="small"
                            onClick={handleOpenChangeEmailModal}
                          >
                            <EditIcon fontSize="small" /> Change
                          </AdornmentButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="start">
                      <Tooltip title="Confirmed">
                        <CheckIcon fontSize="small" color="success" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}

          {mlsIdField && (
            <Grid item sm={6} xs={12}>
              {mlsIdField.readOnly ? (
                <ReadOnlyInfo
                  label={mlsIdField.label}
                  value={defaultValues.mlsId}
                />
              ) : (
                <TextField
                  label={getFieldLabel("mlsId")}
                  name="mlsId"
                  inputProps={{ ...register("mlsId") }}
                  fieldError={errors.mlsId}
                />
              )}
            </Grid>
          )}

          {officeNameField && (
            <Grid item sm={6} xs={12}>
              {officeNameField.readOnly ? (
                <ReadOnlyInfo
                  label={officeNameField.label}
                  value={defaultValues.officeName}
                />
              ) : (
                <TextField
                  label={getFieldLabel("officeName")}
                  name="officeName"
                  inputProps={{ ...register("officeName") }}
                  fieldError={errors.officeName}
                />
              )}
            </Grid>
          )}
        </Grid>

        <Grid container item justifyContent={"flex-end"}>
          <SubscriptionSubmitButton
            isLoading={requestIsLoading}
            loadingText="Processing..."
          >
            {submitButtonText}
          </SubscriptionSubmitButton>
        </Grid>
      </Form>

      <SubscriptionFlowChangeEmail
        open={openChangeEmailModal}
        onClose={() => setOpenChangeEmailModal(false)}
        subscriptionId={subscriptionId}
        currentEmail={defaultValues.email}
      />
    </div>
  );
};
